<script setup lang="ts">
import { definePage } from "unplugin-vue-router/runtime";
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useAbility } from "@casl/vue";
import { useDisplay } from "vuetify";

import PageHeader from "@/components/ui/headers/PageHeader.vue";
import { useActiveSubNavTitle } from "@/components/shared/navbar/useNavbarSubItems";
import BtnToRooms from "@/components/shared/BtnToRooms.vue";
import GeneralInfiniteScrollTable from "@/components/shared/table/GeneralInfiniteScrollTable.vue";
import TableFilterTabs from "@/components/shared/table/TableFilterTabs.vue";
import ConsultationStatusCell from "@/components/models/patient/table/ConsultationStatusCell.vue";
import ReferralStatusCell from "@/components/models/patient/table/ReferralStatusCell.vue";
import PatientTableCellName from "@/components/models/patient/table/PatientTableCellName.vue";
import PatientTableCellAction from "@/components/models/patient/table/PatientTableCellAction.vue";
import TableCellEmpty from "@/components/shared/table/TableCellEmpty.vue";
import ReferralPartyCell from "@/components/models/referral/ReferralPartyCell.vue";

import {
  EReferralDashboardTab,
  useReferralDashboard,
  useReferralReceiverPractices,
  useReferralTabFilters,
  referralStatusPatient,
} from "@/composables/models/treatmentPlan/useReferralDashboard.ts";
import type {
  IReferralDashboardFilters,
  TReferralDashboardItem,
} from "@/api/models/treatmentPlan/referralDashboard";
import type { TFilter } from "@/types/filter/tableFilters.types.ts";

import { formatDatetime, toCamelCase } from "@/utils/Formatters.ts";
import { tableRowClick } from "@/utils/tableRowClick";
import FilterSelect from "@/components/ui/inputs/FilterSelect.vue";
import FilterDateRange from "@/components/ui/inputs/FilterDateRange.vue";
import { practiceSelectable } from "@/api/models/practice/practice";
import { EConsultationStatus } from "@/api/models/referral/consultation";
import { consultationStatusMeta } from "@/composables/models/referral/useReferralStatus.ts";
import { EReferralStatus } from "@/api/models/referral/referral/types";
import { addPatientAction } from "@/components/ui/headers/pageHeaderActions.ts";

const { t } = useI18n();
const { can } = useAbility();
const router = useRouter();
const { mdAndDown } = useDisplay();

definePage({
  meta: {
    title: "generic.entity.dashboard",
    privilegeScope: "section",
    checkPrivileges: () => {
      const { can } = useAbility();
      return can($permActions.READ, $permSubjects.PATIENT__PATIENT);
    },
  },
});

const headerActions = computed(() => addPatientAction(t, can));

const pageTitle = useActiveSubNavTitle();

/**
 * Receiver office options. The whole set is fetched once and passed as static items, so the
 * select filters locally and a deep-linked value resolves on first paint.
 */
const { data: receiverPractices, isLoading: isLoadingReceiverPractices } =
  useReferralReceiverPractices();

const receiverPracticeItems = computed(() =>
  (receiverPractices.value ?? []).map((practice) => practiceSelectable(practice))
);

const referralStatusItems = computed(() =>
  Object.values(EConsultationStatus).map((status) => {
    const label = t(`models.patient.status.${toCamelCase(status)}`);
    const [title, ...subtitleParts] = label.split("-");

    const meta = consultationStatusMeta[status];

    return {
      title: title?.trim() ?? "ERROR: No title for status",
      subtitle: subtitleParts.join(" ").trim() || undefined,
      value: status,
      icon: meta?.icon,
      iconColor: meta?.color,
    };
  })
);

const tabFilters = useReferralTabFilters();
const activeTab = ref<TFilter<EReferralDashboardTab, IReferralDashboardFilters> | null>(null);

const mobilePinnedKeys = computed<string[]>(() =>
  activeTab.value?.tab === EReferralDashboardTab.ACTIONS_NEEDED ? ["action"] : []
);

const headers = [
  {
    key: "patient",
    translationKey: "models.scheduling.addEditEventForm.patient",
    sortable: true,
    sortKey: "last_name",
    width: "20%",
  },
  {
    key: "action",
    translationKey: "models.patient.table.headers.action",
    sortable: false,
    width: "16%",
  },
  {
    key: "status",
    translationKey: "generic.fields.status",
    sortable: false,
    width: "18%",
  },
  {
    key: "consultation_date",
    translationKey: "models.patient.table.headers.consultDate",
    sortable: true,
    sortKey: "current_appointment_start",
    width: "15%",
  },
  {
    key: "sender",
    translationKey: "generic.fields.types.owner",
    sortable: false,
    width: "15%",
  },
  {
    key: "receiver",
    translationKey: "generic.fields.types.referral_recivier",
    sortable: false,
    width: "15%",
  },
  {
    key: "date_referral_sent",
    translationKey: "models.patient.table.headers.dateReferralSent",
    sortable: true,
    sortKey: "date_referral_sent",
    width: "16%",
  },
];

// Search/sort live on the query (`url` mode); the UI filters are declared as `fields` on the
// composable, so the query owns their state, their URL sync and the request filters they map to
// — the page only binds `query.f.*` in the template.
const query = useReferralDashboard({
  url: { useQuery: true, headers },
  initial: { page_size: 25 },
  tabs: activeTab,
  // `TableFilterTabs` resolves the active tab (from `?tab=` or its first
  // filter) right after mount. Waiting for it avoids firing an extra request
  // with no tab filter, which would immediately be superseded.
  enabled: computed(() => activeTab.value !== null),
});

// `date_referral_sent` is dropped on tablet (and below) to keep the table from
// overflowing at those widths; useTableHeaderConfig only reads this list on
// mount, so the omission is fixed at load time rather than tracking live resizes.
const tableInitialKeys = computed<string[]>(() =>
  [
    "patient",
    "action",
    "status",
    "sender",
    "receiver",
    "date_referral_sent",
    "consultation_date",
  ].filter((key) => key !== "date_referral_sent" || !mdAndDown.value)
);

const onRowClick = async (event: MouseEvent, row: Record<"item", TReferralDashboardItem>) => {
  if (row.item.patient?.id) {
    await tableRowClick(event, router, {
      name: "/rooms/patient/[id]",
      params: { id: row.item.patient.id },
    });
  }
};
</script>

<template>
  <div
    class="tw:relative"
    data-tour="dashboard-header"
  >
    <btn-to-rooms />

    <page-header
      :title="pageTitle"
      search-data-tour="dashboard-search"
      :search-placeholder="t('generic.tables.searchByNameOrEmail')"
      :actions="headerActions"
      stacked-filters
    >
      <template #filters>
        <filter-select
          v-model="query.f.referralStatus"
          :items="referralStatusItems"
          :label="t('generic.fields.status')"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[17%] tw:sm:min-w-[14rem]"
        />

        <filter-select
          v-model="query.f.receiverPractice"
          :items="receiverPracticeItems"
          :loading="isLoadingReceiverPractices"
          :label="t('generic.fields.types.referral_recivier')"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[17%] tw:sm:min-w-[14rem]"
        />

        <filter-date-range
          v-model="query.f.consultationDate"
          field-name="consultation_date"
          :label="t('models.patient.table.headers.consultDate')"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[17%] tw:sm:min-w-[14rem]"
        />

        <filter-date-range
          v-model="query.f.dateReferralSent"
          field-name="date_referral_sent"
          :label="t('models.patient.table.headers.dateReferralSent')"
          :max="new Date()"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[17%] tw:sm:min-w-[14rem]"
        />

        <filter-date-range
          v-model="query.f.lastUpdate"
          field-name="last_update"
          :label="t('models.patient.table.filter.lastUpdate')"
          :max="new Date()"
          class="tw:w-full tw:sm:flex-none tw:sm:basis-[17%] tw:sm:min-w-[14rem]"
        />
      </template>
    </page-header>

    <div class="tw:rounded-[10px]">
      <div data-tour="dashboard-overview">
        <table-filter-tabs
          v-model:active-tab="activeTab"
          :filters="tabFilters"
          :loading="query.isLoading.value"
          query-key="tab"
        />
      </div>

      <div
        data-tour="dashboard-main-container"
        class="tw:rounded-[10px]"
      >
        <div class="tw:relative">
          <general-infinite-scroll-table
            :headers="headers"
            :initial-keys="tableInitialKeys"
            :query="query"
            :row-click="onRowClick"
            elevated-animation
            variant="dashboard"
            responsive
            mobile-layout="fields"
            :mobile-header-keys="['patient']"
            mobile-clickable
            data-test-id="referral-table"
            :row-props="{
              'data-test-id': 'referral-table-item',
              'data-tour': 'dashboard-patient-row',
            }"
            :prefetch-viewports="0.9"
            mobile-expandable
            :mobile-pinned-keys="mobilePinnedKeys"
          >
            <template #mobile-header="{ item }">
              <patient-table-cell-name
                :patient="item.patient"
                variant="mobile-header"
              />
            </template>

            <template #[`item.patient`]="{ item }">
              <patient-table-cell-name :patient="item.patient" />
            </template>

            <template #[`item.action`]="{ item }">
              <patient-table-cell-action :jobs="item.jobs_available_for_user" />
            </template>

            <template #[`item.status`]="{ item }">
              <consultation-status-cell
                v-if="item.referral?.status === EReferralStatus.NEW"
                :consultation="item.referral?.consultation"
              />
              <referral-status-cell
                v-else
                :patient="referralStatusPatient(item)"
                unavailable-label-key="models.referral.referrals.status.notStarted"
              />
            </template>

            <template #[`item.sender`]="{ item }">
              <referral-party-cell
                side="sender"
                :item="item"
                :empty-text="t('generic.status.none')"
              />
            </template>

            <template #[`item.receiver`]="{ item }">
              <referral-party-cell
                side="receiver"
                :item="item"
                :empty-text="t('generic.status.none')"
              />
            </template>

            <template #[`item.date_referral_sent`]="{ item }">
              <div
                v-if="item.date_referral_sent"
                class="tw:text-xs tw:whitespace-nowrap"
              >
                {{ formatDatetime(item.date_referral_sent) }}
              </div>
              <table-cell-empty v-else />
            </template>
            <template #[`item.consultation_date`]="{ item }">
              <div
                v-if="
                  item.current_appointment_start && item.referral?.status === EReferralStatus.NEW
                "
                class="tw:text-xs tw:whitespace-nowrap"
              >
                {{ formatDatetime(item.current_appointment_start, undefined, true) }}
              </div>
              <table-cell-empty v-else />
            </template>
          </general-infinite-scroll-table>
        </div>
      </div>
    </div>
  </div>
</template>
